import React, { useState } from 'react';
import { AlertCircle, Eye, Package, Layers, Calendar, User, FileText, Filter, Plus } from 'lucide-react';
import { useApp } from '../App';
import { pendingOrdersService } from '../services/api';

const PendingOrders = () => {
  const { db, setDb } = useApp();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('todos'); // Filtro por estado
  const [filterUser, setFilterUser] = useState('todos'); // Filtro por usuario creador

  // Estado para el formulario de crear pedido
  const [formData, setFormData] = useState({
    salaVenta: '',
    nombreAsesor: '',
    fechaEntrega: '',
    numeroFactura: '',
    descripcionEntrega: '',
    estadoEntrega: 'pendiente'
  });

  // Enriquecer pedidos con campos de visualización
  const enrichPendingOrder = (order) => {
    return {
      ...order,
      salaVenta: order.salaVenta || order.cliente || order.customer,
      nombreAsesor: order.nombreAsesor,
      numeroFactura: order.numeroFactura,
      fechaEntrega: order.fechaEntrega || order.fecha_entrega,
      descripcionEntrega: order.descripcionEntrega || order.producto || order.observaciones,
      customer: order.salaVenta || order.cliente || order.customer,
      status: order.status || order.estado || 'pendiente',
      module: 'PENDIENTE', 
      moduleColor: 'text-orange-600', 
      moduleBg: 'bg-orange-100'
    };
  };

  // Aplicar filtro por estado
  const filterOrdersByStatus = (orders) => {
    if (filterStatus === 'todos') {
      return orders;
    }
    return orders.filter(order => (order.estado || order.status || order.estadoEntrega) === filterStatus);
  };

  // Aplicar filtro por usuario
  const filterOrdersByUser = (orders) => {
    if (filterUser === 'todos') {
      return orders;
    }
    return orders.filter(order => order.createdBy === filterUser);
  };

  // Obtener lista única de usuarios creadores
  const uniqueUsers = [...new Set((db.pendingOrders || []).map(o => o.createdBy).filter(Boolean))];

  // Mostrar pedidos enriquecidos y filtrados (más recientes primero)
  const filteredOrders = filterOrdersByUser(filterOrdersByStatus(
    (db.pendingOrders || [])
      .map(enrichPendingOrder)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  ));

  const viewOrderDetails = (order) => {
    console.log('👁️ Visualizando detalles del pedido:', order);
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
    switch(normalizedStatus) {
      case 'entregado': 
        return 'bg-green-100 text-green-800';
      case 'pendiente': 
        return 'bg-yellow-100 text-yellow-800';
      case 'en_proceso': 
      case 'en-proceso':
        return 'bg-blue-100 text-blue-800';
      case 'listo_para_entrega':
      case 'listo-para-entrega':
      case 'listo':
        return 'bg-purple-100 text-purple-800';
      default: 
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
    switch(normalizedStatus) {
      case 'entregado': 
        return 'Entregado';
      case 'pendiente': 
        return 'Pendiente';
      case 'en_proceso': 
      case 'en-proceso':
        return 'En Proceso';
      case 'listo_para_entrega':
      case 'listo-para-entrega':
      case 'listo':
        return 'Listo para Entrega';
      default: 
        return 'Pendiente';
    }
  };

  const calculateDaysOverdue = (fechaEntrega) => {
    if (!fechaEntrega) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reiniciar a medianoche
    
    // Parsear la fecha sin conversión de zona horaria
    const dateStr = fechaEntrega.split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const deliveryDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    const diffTime = today - deliveryDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveNewOrder = async () => {
    console.log('🔄 Validando y creando pedido pendiente...');
    
    if (!formData.salaVenta.trim()) {
      console.error('❌ Validation: Por favor ingrese la sala de venta');
      alert('Por favor ingrese la sala de venta');
      return;
    }
    if (!formData.nombreAsesor.trim()) {
      console.error('❌ Validation: Por favor ingrese el nombre del asesor');
      alert('Por favor ingrese el nombre del asesor');
      return;
    }
    if (!formData.fechaEntrega) {
      console.error('❌ Validation: Por favor seleccione la fecha de entrega');
      alert('Por favor seleccione la fecha de entrega');
      return;
    }
    if (!formData.numeroFactura.trim()) {
      console.error('❌ Validation: Por favor ingrese el número de factura');
      alert('Por favor ingrese el número de factura');
      return;
    }
    if (!formData.descripcionEntrega.trim()) {
      console.error('❌ Validation: Por favor ingrese la descripción de la entrega');
      alert('Por favor ingrese la descripción de la entrega');
      return;
    }

    try {
      console.log('🔄 Creando pedido pendiente en backend...');
      
      // Enviar solo la fecha en formato YYYY-MM-DD (sin hora)
      // Esto evita cualquier problema de zona horaria
      const newPendingOrder = {
        // Estructura del backend
        cliente: formData.salaVenta,
        telefono: '', 
        producto: formData.descripcionEntrega,
        precio: 0,  
        anticipo: 0,
        fecha_entrega: formData.fechaEntrega, // Ya está en formato YYYY-MM-DD
        observaciones: formData.descripcionEntrega, // Aquí va la descripción del usuario
        estado: 'pendiente',
        // Campos técnicos separados
        numeroFactura: formData.numeroFactura,
        nombreAsesor: formData.nombreAsesor
      };

      console.log('📝 Datos del pedido a enviar:', newPendingOrder);

      // Crear en el backend
      const createdOrder = await pendingOrdersService.createPendingOrder(newPendingOrder);
      console.log('✅ Pedido creado en backend:', createdOrder);

      // Enriquecer el pedido creado con campos de visualización
      const enrichedOrder = {
        ...createdOrder,
        // Campos para la visualización en la tabla y modal
        salaVenta: formData.salaVenta,
        nombreAsesor: formData.nombreAsesor,
        numeroFactura: formData.numeroFactura,
        fechaEntrega: formData.fechaEntrega,
        descripcionEntrega: formData.descripcionEntrega,
        customer: formData.salaVenta,
        status: 'pendiente',
        estadoEntrega: 'pendiente',
        module: 'PENDIENTE',
        moduleColor: 'text-orange-600',
        moduleBg: 'bg-orange-100'
      };

      console.log('📝 Pedido enriquecido para visualización:', enrichedOrder);

      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        pendingOrders: [...(prevDb.pendingOrders || []), enrichedOrder]
      }));

      // Limpiar formulario
      setFormData({
        salaVenta: '',
        nombreAsesor: '',
        fechaEntrega: '',
        numeroFactura: '',
        descripcionEntrega: '',
        estadoEntrega: 'pendiente'
      });

      setShowCreateModal(false);
      
      console.log('✅ Pedido pendiente creado y guardado exitosamente');

      // Mostrar notificación de éxito
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-orange-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center gap-2';
      notification.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span>✅ Pedido pendiente guardado en base de datos</span>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('❌ Error creando pedido pendiente:', error);
      alert('❌ Error al crear el pedido pendiente: ' + error.message);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <AlertCircle className="text-orange-500" size={32} />
            Pedidos Pendientes
          </h2>
          <p className="text-gray-600 mt-2">
            Gestión exclusiva de pedidos creados desde este módulo
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Crear Pedido Pendiente
          </button>
        </div>
      </div>

      {/* Estadísticas Resumen */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <div className="bg-red-50 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <Calendar size={24} className="text-red-600" />
            <h3 className="font-semibold text-red-800">Vencidos</h3>
          </div>
          <p className="text-3xl font-bold text-red-600">
            {filteredOrders.filter(order => calculateDaysOverdue(order.fechaEntrega)).length}
          </p>
        </div>
      </div>

      {/* Tabla de Pedidos Pendientes */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Lista de Pedidos Pendientes</h3>
          <div className="text-sm text-gray-500">
            Mostrando {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''} pendiente{filteredOrders.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Filtro por Estado */}
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Filtrar por Estado:
            </label>
            <select
              value={filterStatus}
              onChange={(e) => {
                console.log('🔍 Filtro de estado cambiado a:', e.target.value);
                setFilterStatus(e.target.value);
              }}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all min-w-[180px]"
            >
              <option value="todos">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="en-proceso">En Proceso</option>
              <option value="listo">Listo para Entrega</option>
              <option value="entregado">Entregado</option>
            </select>
          </div>
          
          {/* Filtro por Usuario Creador */}
          <div className="flex items-center gap-3">
            <User size={18} className="text-gray-500" />
            <label className="text-sm font-medium text-gray-700">
              Filtrar por Creador:
            </label>
            <select
              value={filterUser}
              onChange={(e) => {
                console.log('🔍 Filtro de usuario cambiado a:', e.target.value);
                setFilterUser(e.target.value);
              }}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all min-w-[150px]"
              data-testid="filter-user-pending"
            >
              <option value="todos">Todos los usuarios</option>
              {uniqueUsers.map((user, index) => (
                <option key={index} value={user}>{user}</option>
              ))}
            </select>
          </div>
          
          <span className="text-sm text-gray-500">
            Mostrando: {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-4 text-gray-600 font-medium">Sala de Venta</th>
                <th className="text-left p-4 text-gray-600 font-medium">Asesor</th>
                <th className="text-left p-4 text-gray-600 font-medium">Número de Factura</th>
                <th className="text-left p-4 text-gray-600 font-medium">Fecha Entrega</th>
                <th className="text-left p-4 text-gray-600 font-medium">Estado</th>
                <th className="text-center p-4 text-gray-600 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-12 text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <AlertCircle size={48} className="text-gray-300" />
                      <span className="text-lg">No hay pedidos pendientes</span>
                      <span className="text-sm">
                        Use el botón "Crear Pedido Pendiente" para agregar nuevos pedidos
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrders.map(order => {
                  const daysOverdue = calculateDaysOverdue(order.fechaEntrega);
                  return (
                    <tr key={order.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-gray-400" />
                          <span className="font-medium">{order.cliente || order.customer || order.salaVenta || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="p-4 text-gray-600">{order.nombreAsesor || 'N/A'}</td>
                      <td className="p-4 text-gray-600">{order.numeroFactura || 'N/A'}</td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="text-gray-800">
                            {order.fechaEntrega ? (() => {
                              const dateStr = order.fechaEntrega;
                              const datePart = dateStr.split('T')[0];
                              const [year, month, day] = datePart.split('-');
                              const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                              return date.toLocaleDateString('es-CO');
                            })() : 'N/A'}
                          </span>
                          {daysOverdue && (
                            <span className="text-xs text-red-600 font-medium">
                              {daysOverdue} día{daysOverdue !== 1 ? 's' : ''} de retraso
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status || order.estadoEntrega)}`}>
                          {getStatusText(order.status || order.estadoEntrega)}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => viewOrderDetails(order)}
                          className="text-blue-500 hover:text-blue-700 transition-colors p-2 rounded hover:bg-blue-50"
                          title="Ver detalles"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Crear Pedido Pendiente */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-orange-600">Crear Pedido Pendiente</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sala de Venta *
                  </label>
                  <input
                    type="text"
                    value={formData.salaVenta}
                    onChange={(e) => handleInputChange('salaVenta', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    placeholder="Ej: Sala Principal, Sala Norte..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Asesor *
                  </label>
                  <input
                    type="text"
                    value={formData.nombreAsesor}
                    onChange={(e) => handleInputChange('nombreAsesor', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    placeholder="Nombre completo del asesor"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Entrega *
                  </label>
                  <input
                    type="date"
                    value={formData.fechaEntrega}
                    onChange={(e) => handleInputChange('fechaEntrega', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Factura *
                  </label>
                  <input
                    type="text"
                    value={formData.numeroFactura}
                    onChange={(e) => handleInputChange('numeroFactura', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    placeholder="Ej: FAC-001, PEND-123..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción de la Entrega *
                </label>
                <textarea
                  value={formData.descripcionEntrega}
                  onChange={(e) => handleInputChange('descripcionEntrega', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                  placeholder="Describe detalladamente qué se debe entregar, especificaciones, observaciones, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado de Entrega
                </label>
                <select
                  value={formData.estadoEntrega}
                  onChange={(e) => handleInputChange('estadoEntrega', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en-proceso">En Proceso</option>
                  <option value="listo">Listo para Entrega</option>
                  <option value="entregado">Entregado</option>
                </select>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={saveNewOrder}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Crear Pedido
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      salaVenta: '',
                      nombreAsesor: '',
                      fechaEntrega: '',
                      numeroFactura: '',
                      descripcionEntrega: '',
                      estadoEntrega: 'pendiente'
                    });
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles - Diseño Nuevo */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl shadow-2xl border border-gray-200">
            {/* Header con botón cerrar */}
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800">
                Pedido #{selectedOrder.numeroFactura || selectedOrder.id || '000'}
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light"
              >
                ✕
              </button>
            </div>

            {/* Badges */}
            <div className="flex gap-4 mb-8">
              {/* Badge Principal - Sala de Venta */}
              <div className="bg-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-lg">
                {selectedOrder.salaVenta || selectedOrder.cliente || selectedOrder.customer || 'SALA DE VENTA'}
              </div>
              
              {/* Badge Estado */}
              <div className={`px-6 py-4 rounded-2xl font-medium flex items-center gap-2 ${getStatusColor(selectedOrder.estado || selectedOrder.status || selectedOrder.estadoEntrega).replace('bg-', 'bg-').replace('text-', 'text-')}`}>
                <div className={`w-3 h-3 rounded-full ${
                  getStatusColor(selectedOrder.estado || selectedOrder.status || selectedOrder.estadoEntrega).includes('green') ? 'bg-green-500' :
                  getStatusColor(selectedOrder.estado || selectedOrder.status || selectedOrder.estadoEntrega).includes('yellow') ? 'bg-yellow-500' :
                  getStatusColor(selectedOrder.estado || selectedOrder.status || selectedOrder.estadoEntrega).includes('blue') ? 'bg-blue-500' :
                  getStatusColor(selectedOrder.estado || selectedOrder.status || selectedOrder.estadoEntrega).includes('purple') ? 'bg-purple-500' :
                  'bg-gray-500'
                }`}></div>
                {getStatusText(selectedOrder.estado || selectedOrder.status || selectedOrder.estadoEntrega)}
              </div>
            </div>

            {/* Información Principal */}
            <div className="space-y-6">
              
              {/* Asesor */}
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm font-medium">Asesor</p>
                  <p className="text-gray-900 text-2xl font-bold">
                    {selectedOrder.nombreAsesor || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Descripción */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mt-1">
                  <FileText size={20} className="text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-600 text-sm font-medium">Descripción</p>
                  <p className="text-gray-900 text-2xl font-bold leading-tight">
                    {selectedOrder.producto || selectedOrder.descripcionEntrega || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Fecha de Entrega */}
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Calendar size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm font-medium">Fecha de Entrega</p>
                  <p className="text-gray-900 text-2xl font-bold">
                    {selectedOrder.fechaEntrega || selectedOrder.fecha_entrega 
                      ? (() => {
                          const dateStr = selectedOrder.fechaEntrega || selectedOrder.fecha_entrega;
                          // Extraer solo la parte de la fecha (YYYY-MM-DD)
                          const datePart = dateStr.split('T')[0];
                          const [year, month, day] = datePart.split('-');
                          // Crear fecha sin conversión de zona horaria
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          return date.toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long', 
                            year: 'numeric'
                          });
                        })()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Información Adicional (si existe) */}
            {(selectedOrder.observaciones || selectedOrder.producto || selectedOrder.descripcionEntrega) && (
              <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-600 text-sm font-medium mb-2">Observaciones</p>
                <p className="text-gray-900 text-2xl font-bold">
                  {selectedOrder.observaciones || selectedOrder.producto || selectedOrder.descripcionEntrega || 'Sin observaciones'}
                </p>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default PendingOrders;